import { LineItem, Money, Quote, SalesItemCode, SalesItemSubType, SalesItemType, Segment } from "../Types";
import { LineItemIsExtraNight } from "../Helpers/LineItemHelper";

/** Segment index that owns the line item (for PATCH paths on multi-segment quotes). */
export function findSegmentIndexContainingLineItem(draft: Quote, lineItemId: string): number {
  return draft.segments.findIndex((seg) => seg.lineItems?.some((li) => li.id === lineItemId));
}

/** Optional add-ons for the upgrade UI: one segment, or every segment merged for multi-quote trips. */
export function getOptionalItemsForUpgradeUi(
  currentSegmentIndex: number,
  quote: Quote,
  draft: Quote,
  isMultiQuote: boolean
): Array<LineItem> {
  if (!isMultiQuote) {
    return getOptionalItems(currentSegmentIndex, quote, draft);
  }
  return draft.segments.flatMap((_, idx) => getOptionalItems(idx, quote, draft));
}

export function getOptionalItems(segment: number, quote?: Quote, draft?: Quote): Array<LineItem> {
  if (!quote || !draft) {
    return [];
  }
  if (segment >= quote.segments.length) {
    return [];
  }
  const currentSegment = quote.segments[segment];
  const currentDraftSegment = draft.segments[segment];
  const flightSalesItemCodes = Object.values(SalesItemCode);

  const optionalItems = currentSegment?.lineItems.filter((lineItem) => {
    const isFlight = lineItem.subType === SalesItemSubType.Flight;
    const isAllowedFlight = isFlight && flightSalesItemCodes.includes(lineItem.code as SalesItemCode);
    const isNonFlightOptional = !isFlight && lineItem.isOptional;
    const isAccommodationExtraNight = lineItem.type === SalesItemType.Accommodation && LineItemIsExtraNight(lineItem);

    return (
      lineItem.type !== SalesItemType.Course &&
      !isAccommodationExtraNight &&
      lineItem.subType !== SalesItemSubType.Discount &&
      lineItem.totalPrice.amount > 0 &&
      (isAllowedFlight || isNonFlightOptional)
    );
  });

  const selectedMutuallyExclusiveFlight = currentDraftSegment?.lineItems.find(
    (lineItem) =>
      flightSalesItemCodes.includes(lineItem.code as SalesItemCode) &&
      !lineItem.isOptional &&
      optionalItems?.some((optionalItem) => optionalItem.sku === lineItem.sku)
  );

  return currentDraftSegment?.lineItems.filter((lineItem) => {
    const isOptionalItem = optionalItems?.some((optionalItem) => optionalItem.sku === lineItem.sku);
    if (!isOptionalItem) {
      return false;
    }

    if (
      selectedMutuallyExclusiveFlight &&
      flightSalesItemCodes.includes(lineItem.code as SalesItemCode) &&
      lineItem.code !== selectedMutuallyExclusiveFlight.code
    ) {
      return false;
    }

    return true;
  });
}

function calculateCost(itemSkus: string[], quoteLineItems: LineItem[], optionalItem: boolean): number {
  return quoteLineItems
    .filter((lineItem) => itemSkus.some((optionalItemSku) => optionalItemSku === lineItem.sku))
    .filter((lineItem) => lineItem.isOptional === optionalItem)
    .reduce((sum, lineItem) => sum + lineItem.totalPrice.amount, 0);
}

export function priceDifferenceForOptionalItems(segment: number, quote?: Quote, draft?: Quote): number {
  if (!quote || !draft || segment >= quote.segments.length) {
    return 0;
  }
  const optionalItemSkus = getOptionalItems(segment, quote, draft).map((x) => x.sku);
  const optionalItemsCostDraft = calculateCost(optionalItemSkus, draft.segments[segment].lineItems, false);
  const optionalItemsCostQuote = calculateCost(optionalItemSkus, quote.segments[segment].lineItems, true);

  return optionalItemsCostDraft - optionalItemsCostQuote;
}

export function priceDifferenceForCourseDuration(segment: number, quote?: Quote, draft?: Quote): number {
  if (!quote || !draft || segment >= quote.segments.length) {
    return 0;
  }
  const priceDifferenceOptionalItems = priceDifferenceForOptionalItems(segment, quote, draft);
  const totalPriceDifference =
    draft.segments[segment].price.totalPrice.amount - quote.segments[segment].price.totalPrice.amount;
  return totalPriceDifference - priceDifferenceOptionalItems;
}

export function getAccommodationOptions(segment: Segment): Array<LineItem> {
  return segment.lineItems.filter(
    (lineItem) => lineItem.type === SalesItemType.Accommodation && !LineItemIsExtraNight(lineItem)
  );
}

export function getSelectedAccommodation(segment: Segment): LineItem | undefined {
  return segment.lineItems.find((lineItem) => lineItem.type == SalesItemType.Accommodation && !lineItem.isOptional);
}

export function getPriceDifferenceForChoosingAccommodation(
  segment: Segment,
  accommodationSku: string,
  resultAsTotal: boolean = true
): Money {
  const wishedAccommodationCost = segment.lineItems.find(
    (lineItem) => lineItem.type === SalesItemType.Accommodation && lineItem.sku === accommodationSku
  );

  const selectedAccommodationCost = segment.lineItems.find(
    (lineItem) =>
      lineItem.type === SalesItemType.Accommodation && lineItem.sku === getSelectedAccommodation(segment)?.sku
  );

  const difference = resultAsTotal
    ? (wishedAccommodationCost?.totalPrice?.amount ?? 0) - (selectedAccommodationCost?.totalPrice?.amount ?? 0)
    : (wishedAccommodationCost?.price?.amount ?? 0) - (selectedAccommodationCost?.price?.amount ?? 0);

  return {
    amount: difference,
    currency: (wishedAccommodationCost?.totalPrice?.currency ?? selectedAccommodationCost?.totalPrice?.currency)!,
  };
}

export function getTotalAfterChoosingAccommodation(segment: Segment, accommodationSku: string): Money {
  const changeCost = getPriceDifferenceForChoosingAccommodation(segment, accommodationSku, true);
  const totalCostBeforeChange = segment.price.totalPrice;

  return {
    amount: totalCostBeforeChange.amount + changeCost.amount,
    currency: totalCostBeforeChange.currency,
  };
}
